/**
 * Shifts API
 * CRUD operations for user shifts (Schichtplanung)
 */

import { handleSupabaseError, supabase } from './client'
import { getRowsFiltered, type ListParams, type ListResponse } from '@/api/index'

export interface UserShift {
    id?: number
    user_id: string
    date: string        // YYYY-MM-DD
    shift_type: 'early' | 'late' | 'night' | 'custom' | 'none'
    start_time?: string | null  // HH:mm:ss, nur bei 'custom'
    end_time?: string | null    // HH:mm:ss, nur bei 'custom'
    created_at?: string
    updated_at?: string
}

/**
 * Get shifts for specific users in a date range
 * Uses direct Supabase query with .in() to avoid the filter_rows RPC uppercasing
 * user_id values in OR lists, which would cause case-sensitive mismatches
 * (user_ids are stored lowercase, but filter_rows IN lists uppercase all values).
 */
export async function getShiftsForRange(
    userIds: string[],
    dateStart: string,
    dateEnd: string
): Promise<UserShift[]> {
    if (userIds.length === 0) return []

    const { data, error } = await supabase
        .from('user_shifts')
        .select('*')
        .in('user_id', userIds)
        .gte('date', dateStart)
        .lte('date', dateEnd)
        .order('date', { ascending: true })

    if (error) handleSupabaseError(error)
    return data || []
}

/**
 * Upsert a single shift (insert or update on conflict)
 */
export async function upsertShift(shift: Pick<UserShift, 'user_id' | 'date' | 'shift_type' | 'start_time' | 'end_time'>): Promise<UserShift> {
    const { data, error } = await supabase
        .from('user_shifts')
        .upsert({
            user_id: shift.user_id,
            date: shift.date,
            shift_type: shift.shift_type,
            start_time: shift.shift_type === 'custom' ? shift.start_time : null,
            end_time: shift.shift_type === 'custom' ? shift.end_time : null,
        }, {
            onConflict: 'user_id,date',
        })
        .select()
        .single()

    if (error) handleSupabaseError(error)
    return data!
}

/**
 * Upsert multiple shifts at once (batch)
 */
export async function upsertShiftsBatch(shifts: Pick<UserShift, 'user_id' | 'date' | 'shift_type' | 'start_time' | 'end_time'>[]): Promise<UserShift[]> {
    if (shifts.length === 0) return []

    const payload = shifts.map(s => ({
        user_id: s.user_id,
        date: s.date,
        shift_type: s.shift_type,
        start_time: s.shift_type === 'custom' ? s.start_time : null,
        end_time: s.shift_type === 'custom' ? s.end_time : null,
    }))

    const { data, error } = await supabase
        .from('user_shifts')
        .upsert(payload, {
            onConflict: 'user_id,date',
        })
        .select()

    if (error) handleSupabaseError(error)
    return data || []
}

/**
 * Delete a shift
 */
export async function deleteShift(userId: string, date: string): Promise<void> {
    const { error } = await supabase
        .from('user_shifts')
        .delete()
        .eq('user_id', userId)
        .eq('date', date)

    if (error) handleSupabaseError(error)
}

/**
 * Get shifts filtered (for list views, paginated)
 */
export async function getShiftsFiltered(params: ListParams): Promise<ListResponse<UserShift>> {
    return getRowsFiltered<UserShift>(params, 'user_shifts')
}
